// src/index.ts
import { Observable, asyncScheduler, BehaviorSubject, switchMap, timer, tap, Subject, filter, throttleTime, merge, take } from "rxjs";
var DEFAULT_INACTIVE_INTERVAL = 20 * 60 * 1e3;
var WebsiteVisibilityObserver = class {
  constructor(window2) {
    this.window = window2;
    this.timerHandle = 0;
    this.isHiddenDelay = 15 * 1e3;
    this.updateIsHidden = () => {
      const { hidden } = this.window.document;
      if (hidden === this.hidden) {
        this.stopTimer();
      } else if (!hidden) {
        this.hidden = hidden;
        this.isHidden$.next(hidden);
      } else {
        this.startTimer();
      }
    };
    var _a;
    this.isHidden$ = new Subject();
    if ((_a = this.window) == null ? void 0 : _a.document) {
      this.hidden = this.window.document.hidden;
      this.window.addEventListener("visibilitychange", this.updateIsHidden);
    } else {
      this.hidden = false;
    }
  }
  startTimer() {
    this.stopTimer();
    this.timerHandle = this.window.setTimeout(() => {
      this.hidden = true;
      this.isHidden$.next(this.hidden);
      this.timerHandle = 0;
    }, this.isHiddenDelay);
  }
  isHidden() {
    return this.hidden;
  }
  stopTimer() {
    if (this.timerHandle) {
      this.window.clearTimeout(this.timerHandle);
      this.timerHandle = 0;
    }
  }
};
function smartTimerFactory(window2) {
  const visibilityObserver = new WebsiteVisibilityObserver(window2);
  const smartTimer2 = (dueTime, intervalDuration = 0, inactiveIntervalDuration = -1) => {
    let calculatedInactiveIntervalDuration = inactiveIntervalDuration;
    if (inactiveIntervalDuration < 0) {
      calculatedInactiveIntervalDuration = intervalDuration > DEFAULT_INACTIVE_INTERVAL ? intervalDuration : DEFAULT_INACTIVE_INTERVAL;
    }
    if (intervalDuration === 0) {
      calculatedInactiveIntervalDuration = 0;
    }
    return new Observable((subscriber) => {
      let n = 0;
      const timer$ = new BehaviorSubject(dueTime);
      let trigger$;
      if (intervalDuration === 0) {
        trigger$ = timer$.pipe(
          switchMap((value) => timer(value)),
          take(1)
        );
      } else {
        trigger$ = merge(
          // This is supposed to mimic a configurable interval 
          timer$.pipe(
            switchMap((value) => timer(value))
          ),
          // Emit when page becomes visible after being hidden
          visibilityObserver.isHidden$.pipe(
            // Do not emit when visibility changes to hidden
            filter((value) => !value)
          )
        );
      }
      const subscription = trigger$.pipe(
        // No matter visibility changes, only emit every "intervalDuration" at most
        // This is supposed to block unneeded requests for long timers (like 30mins)
        // which could be triggered more frequently than needed when user toggles tab too often
        throttleTime(intervalDuration, asyncScheduler, { leading: true, trailing: true }),
        // Once we get here it is clear that smart timer will emit so we need to
        // ensure that the timer will emit again when time is right
        tap(() => {
          const nextDueTime = visibilityObserver.isHidden() ? calculatedInactiveIntervalDuration : intervalDuration;
          if (nextDueTime > 0) {
            timer$.next(nextDueTime);
          }
        })
      ).subscribe({
        next: () => {
          if (!subscriber.closed) {
            subscriber.next(n);
            n++;
            if (timer$.closed) {
              subscriber.complete();
            }
          }
        },
        complete: () => {
          if (!timer$.closed) {
            timer$.complete();
          }
          if (!subscriber.closed) {
            subscriber.complete();
          }
        }
      });
      subscription.add(() => {
        if (!subscriber.closed) {
          subscriber.complete();
        }
        if (!timer$.closed) {
          timer$.complete();
        }
      });
      return subscription;
    });
  };
  return {
    smartTimer: smartTimer2,
    visibilityObserver
  };
}
var { smartTimer } = smartTimerFactory(window);
export {
  WebsiteVisibilityObserver,
  smartTimer,
  smartTimerFactory
};
